"use client";

import { gsap } from "gsap";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const BlogHeroSection = () => {
  const root = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      tl.from(".hero-eyebrow", {
        y: 20,
        opacity: 0,
        duration: 0.7,
      })
        .from(
          ".hero-title",
          {
            y: 50,
            opacity: 0,
            duration: 1,
            stagger: 0.1,
          },
          "-=0.4",
        )
        .from(
          ".hero-sub",
          {
            y: 30,
            opacity: 0,
            duration: 0.9,
          },
          "-=0.5",
        );

      gsap.to(imageRef.current, {
        yPercent: 10,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }, root);

    return () => ctx.revert();
  }, []);
  const stats = [
    {
      icon: "/images/icons/users.webp",
      title: "Happy\nClients",
      value: "100+",
    },
    {
      icon: "/images/icons/Frame.webp",
      title: "Projects\nDelivered",
      value: "300+",
    },
    {
      icon: "/images/icons/globeIcon.webp",
      title: "Countries\nServed",
      value: "10+",
    },
    {
      icon: "/images/icons/trophyIcon.webp",
      title: "Retained & Referred Clients",
      value: "50+",
    },
  ];
  const gradient = "linear-gradient(110.23deg, #7949CD 6.47%, #6B58F5 44.08%, #0EA7F0 83.15%)";
  return (
    <section ref={root} className="relative hero-section w-full overflow-hidden bg-background-primary !px-4">
      {/* Mobile background (light) */}
      <div className="relative block lg:hidden w-full h-[260px] sm:h-[340px] dark:hidden">
        <Image src="/images/serviceBanners/blogsWh.webp" alt="Blog & Insights" fill priority className="object-cover object-right" />
      </div>
      {/* Mobile background (dark) */}
      <div className="relative hidden w-full h-[260px] sm:h-[340px] dark:block lg:dark:hidden">
        <Image src="/images/serviceBanners/BlogsandInsights.webp" alt="Blog & Insights" fill priority className="object-cover object-right" />
      </div>

      {/* Desktop background (wrapper with ref={imageRef} for GSAP) */}
      <div ref={imageRef} className="absolute inset-0 w-full h-full top-0 hidden lg:block">
        <Image src="/images/serviceBanners/blogsWh.webp" alt="Blog & Insights" fill priority className="object-contain object-right dark:hidden" />
        <Image src="/images/serviceBanners/BlogsandInsights.webp" alt="Blog & Insights" fill priority className="object-contain object-right hidden dark:block" />
      </div>

      {/* Light Overlay */}
      <div
        className="absolute inset-0 z-10 hidden lg:block dark:hidden"
        style={{
          background: "linear-gradient(90deg, rgba(240,242,255,0.95) 0%, rgba(240,242,255,1) 35%, rgba(240,242,255,0.9) 40%, rgba(240,242,255,0.25) 50%, transparent 100%)",
        }}
      />
      {/* Dark Overlay */}
      <div
        className="absolute inset-0 z-10 hidden dark:lg:block"
        style={{
          background: "linear-gradient(90deg, #0d0f1e 0%, #0d0f1e 28%, rgba(13,15,30,1) 45%, rgba(13,15,30,0.3) 65%, transparent 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-20 flex flex-col justify-center px-6! py-0! md:py-15! ">
        <div className="max-w-7xl">
          <div className="flex items-center gap-4 mb-4!">
            <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">TYCHO JOURNAL</h3>

            <span className="w-16 h-[2px] bg-[#8b3dff] rounded-full" />
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6!">
            <span className="hero-title text-text-primary">Blog &amp;</span>

            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600"> Insights</span>
          </h1>

          <p className="text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg md:max-w-[90vw] lg:max-w-[40vw] leading-relaxed !mb-12 w-full">
            Stay ahead in the digital world with expert insights, industry trends, and innovative ideas across web development, mobile applications, UI UX design, emerging
            technologies, and the evolving IT landscape. Our blog provides valuable resources, practical knowledge, and actionable strategies that help businesses and developers
            make informed decisions. Explore in depth articles that enhance technical understanding, inspire innovation, and support continuous learning in a fast changing
            technology environment. We focus on delivering high quality content that empowers growth, improves digital skills, and helps organizations succeed in today’s
            competitive, technology driven era with modern solutions and future ready perspectives.
          </p>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto mb-8! lg:mb-16!">
          <div className="grid grid-cols-2 lg:grid-cols-8 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center group">
                <div className="relative flex items-center justify-center">
                  <div className="relative flex items-center justify-center border border-transparent bg-transparent backdrop-blur-sm group-hover:scale-105 transition-transform duration-300">
                    <img src={item.icon} alt="icons" className="h-14" />
                  </div>
                </div>

                <p
                  className="mt-5! text-2xl font-bold tracking-tighter"
                  style={{
                    background: gradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {item.value}
                </p>
                <h3 className="mt-1! font-medium text-text-secondary lg:text-md text-sm font-medium leading-tight">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          min-height: 80vh;
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-section {
            min-height: 550px;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-section {
            min-height: 500px;
          }
        }

        @media screen and (min-width: 1441px) {
          .hero-section {
            min-height: 650px;
          }
        }
      `}</style>
    </section>
  );
};

export default BlogHeroSection;
